import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Select,
  Space,
  Text,
  Textarea,
  Title,
  createStyles,
  useMantineTheme
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconQuestionMark } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { NotificationService } from 'services/notification-service';
import { SubscriptionService } from 'services/subscription-service';
import { PRIMARY_SHADE_WHITE } from 'shared/constants/theme';
import { CancelationReason, ChurnDataDto, ChurnDiscount, PromoCode } from 'shared/interfaces/Subscription';
import {
  CANCELATION_COMMENT_MAX_LENGTH,
  characterCountColor,
  maxLengthValidator,
  requiredInputValidator
} from 'shared/utils/form-validators';

interface CancelationModalProps {
  isOpen: boolean;
  onClose: () => void;
  isMonthly: boolean;
}

const useStyles = createStyles((theme) => ({
  container: {
    height: '80vh',
    maxHeight: '1000px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row'
  },
  card: {
    width: '170px',
    height: '220px',
    border: `1.5px solid ${theme.colors.gray[3]}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    margin: theme.spacing.lg,

    '&:hover': {
      border: `2px solid ${theme.colors.gray[PRIMARY_SHADE_WHITE]}`,
      cursor: 'pointer'
    }
  },
  slideEnterFromLeft: {
    transform: 'translateX(-100%)'
  },
  slideEnterActiveFromLeft: {
    transform: 'translateX(0%)',
    transition: 'transform 500ms'
  },
  slideExitToLeft: {
    transform: 'translateX(0%)',
    display: 'none'
  },
  slideExitActiveToLeft: {
    transform: 'translateX(100%)',
    transition: 'transform 500ms'
  }
}));

enum ActiveSelector {
  CONFIRMATION,
  FORM,
  DISCOUNT_OFFER
}

export function CancelationModal({ isOpen, onClose, isMonthly }: CancelationModalProps) {
  const { t } = useTranslation();
  const [, setSubscription] = useSubscriptionContext();
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const [activeSelector, setActiveSelector] = useState(ActiveSelector.CONFIRMATION);
  const [churnDiscountData, setChurnDiscountData] = useState<ChurnDiscount>();

  useEffect(() => {
    if (!isOpen) {
      setActiveSelector(ActiveSelector.CONFIRMATION);
      cancelationForm.reset();
    }
  }, [isOpen]);

  const cancelationForm = useForm({
    initialValues: {
      reason: CancelationReason.NULL,
      comment: ''
    },
    validate: {
      reason: (value) => requiredInputValidator(value),
      comment: (value) => maxLengthValidator(value, CANCELATION_COMMENT_MAX_LENGTH)
    }
  });

  const getChurnData = () => {
    return {
      reason: cancelationForm.values.reason === CancelationReason.NULL ? undefined : cancelationForm.values.reason,
      comment: cancelationForm.values.comment === '' ? undefined : cancelationForm.values.comment
    } as ChurnDataDto;
  };

  const close = () => {
    cancelationForm.reset();
    setActiveSelector(ActiveSelector.CONFIRMATION);
    onClose();
  };

  const cancelSubscriptionMutation = useMutation({
    mutationFn: SubscriptionService.cancelSubscription,
    onSuccess: () => {
      // Temporary data since the subscription update is triggered by the webhook.
      // This is done to display the cancelation UI.
      setSubscription((prevSubscription) => ({ ...prevSubscription, cancelAtRenewDate: true }));

      close();
      NotificationService.showResult(true, 'FEED_BACK.SUBSCRIPTION.SUCCESS.CANCEL');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SUBSCRIPTION.FAILURE.CANCEL')
  });

  const cancelSubscription = () => {
    cancelSubscriptionMutation.mutate(getChurnData());
  };

  const getChurnDiscountMutation = useMutation({
    mutationFn: SubscriptionService.getChurnDiscount,
    onSuccess: (churnDiscount: ChurnDiscount) => {
      setChurnDiscountData(churnDiscount);
      if (churnDiscount.discountPercentage > 0) setActiveSelector(ActiveSelector.DISCOUNT_OFFER);
      else cancelSubscription();
    },
    onError: () => cancelSubscription()
  });

  const claimChurnDiscountMutation = useMutation({
    mutationFn: SubscriptionService.claimChurnDiscount,
    onSuccess: () => {
      // Temporary data since the subscription update is triggered by the webhook.
      // This is done to display the cancelation UI.
      setSubscription((prevSubscription) => ({
        ...prevSubscription,
        hasUsedChurnRetentionDiscount: true,
        promoCode: {
          stripeId: '',
          stripeCouponId: '',
          numberOfMonths: churnDiscountData?.numberOfMonths ?? 0,
          discountPercentage: churnDiscountData?.discountPercentage ?? 0
        } as PromoCode
      }));
      close();
      NotificationService.showResult(true, 'FEED_BACK.SUBSCRIPTION.SUCCESS.CLAIM_DISCOUNT');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SUBSCRIPTION.FAILURE.CLAIM_DISCOUNT')
  });

  const getChurnDiscountData = (skipForm = false) => {
    getChurnDiscountMutation.mutate(skipForm ? {} : getChurnData());
  };

  const skipForm = () => getChurnDiscountData(true);

  const submitForm = () => {
    const errors = cancelationForm.validate();
    if (!errors.hasErrors) getChurnDiscountData();
  };

  return (
    <Modal title={t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.TITLE')} size={750} opened={isOpen} onClose={onClose}>
      <Box sx={{ overflow: 'hidden', width: '100%' }}>
        <CSSTransition
          in={activeSelector === ActiveSelector.CONFIRMATION}
          timeout={500}
          classNames={{
            enter: classes.slideEnterFromLeft,
            enterActive: classes.slideEnterActiveFromLeft,
            exit: classes.slideExitToLeft,
            exitActive: classes.slideExitActiveToLeft
          }}
          unmountOnExit>
          <Flex direction="row" align="center" justify="space-between">
            <Text maw={500}>{t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.CONFIRMATION.TITLE')}</Text>
            <Flex direction="row" align="center">
              <Button variant="default" onClick={() => close()}>
                {t('APP.ACTIONS.NO')}
              </Button>
              <Space w={10} />
              <Button onClick={() => setActiveSelector(ActiveSelector.FORM)}>{t('APP.ACTIONS.YES')}</Button>
            </Flex>
          </Flex>
        </CSSTransition>

        <CSSTransition
          in={activeSelector === ActiveSelector.FORM}
          timeout={500}
          classNames={{
            enter: classes.slideEnterFromLeft,
            enterActive: classes.slideEnterActiveFromLeft,
            exit: classes.slideExitToLeft,
            exitActive: classes.slideExitActiveToLeft
          }}
          unmountOnExit>
          <Box>
            <Flex
              w={40}
              ml="auto"
              mr="auto"
              p="xs"
              direction="column"
              align="center"
              justify="center"
              style={{ backgroundColor: theme.colors.gray[1], borderRadius: 10 }}>
              <IconQuestionMark />
            </Flex>
            <Title mt={5} order={4} align="center" maw={400} ml="auto" mr="auto">
              {t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.TITLE')}
            </Title>
            <Text align="center" maw={300} ml="auto" mr="auto" mt={10} color="gray">
              {t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.SUB_TITLE')}
            </Text>
            <Space h={20} />
            <Select
              w="100%"
              label={t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.LABEL')}
              placeholder={t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.PLACEHOLDER')}
              data={[
                {
                  value: CancelationReason.TOO_EXPENSIVE,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.TOO_EXPENSIVE')
                },
                {
                  value: CancelationReason.MISSING_FEATURES,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.MISSING_FEATURES')
                },
                {
                  value: CancelationReason.SWITCHED_SERVICE,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.SWITCHED_SERVICE')
                },
                {
                  value: CancelationReason.CUSTOMER_SERVICE,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.CUSTOMER_SERVICE')
                },
                {
                  value: CancelationReason.TOO_COMPLEX,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.TOO_COMPLEX')
                },
                {
                  value: CancelationReason.UNUSED,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.UNUSED')
                },
                {
                  value: CancelationReason.LOW_QUALITY,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.LOW_QUALITY')
                },
                {
                  value: CancelationReason.OTHER,
                  label: t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.REASONS.OPTIONS.OTHER')
                }
              ]}
              {...cancelationForm.getInputProps('reason')}
            />
            <Space h={10} />
            <Textarea
              minRows={4}
              maxRows={4}
              label={t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.COMMENT.LABEL')}
              placeholder={t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.FORM.COMMENT.PLACEHOLDER')}
              {...cancelationForm.getInputProps('comment')}
            />
            <Text
              size="sm"
              ml={5}
              color={characterCountColor(CANCELATION_COMMENT_MAX_LENGTH, cancelationForm.values.comment.length)}>
              {cancelationForm.values.comment.length}/{CANCELATION_COMMENT_MAX_LENGTH}
            </Text>
            <Group position="right" p="md" mt="xl">
              <Button loading={getChurnDiscountMutation.isLoading} variant="outline" onClick={() => skipForm()}>
                {t('APP.ACTIONS.NO_THANKS')}
              </Button>
              <Button loading={getChurnDiscountMutation.isLoading} onClick={() => submitForm()}>
                {t('APP.ACTIONS.SUBMIT')}
              </Button>
            </Group>
          </Box>
        </CSSTransition>

        <CSSTransition
          in={activeSelector === ActiveSelector.DISCOUNT_OFFER}
          timeout={500}
          classNames={{
            enter: classes.slideEnterFromLeft,
            enterActive: classes.slideEnterActiveFromLeft,
            exit: classes.slideExitToLeft,
            exitActive: classes.slideExitActiveToLeft
          }}
          unmountOnExit>
          <Box>
            <Title mt={20} order={4} align="center" ml="auto" mr="auto">
              {t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.DISCOUNT_OFFER.TITLE')}
            </Title>
            <Space h={10} />
            <Text align="center" ml="auto" mr="auto">
              {t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.DISCOUNT_OFFER.SUB_TITLE')}
            </Text>
            <Space h={30} />
            <Alert
              icon={<Text size={25}>🎉</Text>}
              maw={350}
              ml="auto"
              mr="auto"
              title={<Title order={4}>{t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.DISCOUNT_OFFER.OFFER_TITLE')}</Title>}
              color="green">
              <Title order={5}>
                {t(
                  `SETTINGS.BILLING.CANCEL_SUBSCRIPTION.DISCOUNT_OFFER.${isMonthly ? 'MONTHLY_OFFER' : 'ANNUAL_OFFER'}`,
                  {
                    months: churnDiscountData?.numberOfMonths,
                    percentage: churnDiscountData?.discountPercentage
                  }
                )}
              </Title>
            </Alert>
            <Group position="right" p="md" mt="xl">
              <Button
                loading={claimChurnDiscountMutation.isLoading || cancelSubscriptionMutation.isLoading}
                variant="outline"
                onClick={() => cancelSubscription()}>
                {t('APP.ACTIONS.NO_THANKS')}
              </Button>
              <Button
                loading={claimChurnDiscountMutation.isLoading || cancelSubscriptionMutation.isLoading}
                onClick={() => claimChurnDiscountMutation.mutate(getChurnData())}>
                {t('APP.ACTIONS.CLAIM_DISCOUNT')}
              </Button>
            </Group>
          </Box>
        </CSSTransition>
      </Box>
    </Modal>
  );
}
